/**
 * Concluir o envio da abertura (E144.F2/F4, GUS-105 + GUS-135, ADR-0098 §2.4). Espelha
 * `apps/api/types/conclusao_da_abertura.py`.
 *
 * - `concluirAbertura`: `POST /casos/:id/abertura/conclusao` — um caso por contrato. Repetir devolve
 *   o mesmo recibo e não cria nada.
 * - `lerConclusao`: `GET` da conclusão gravada, para a tela Acompanhar recarregada; `null` enquanto o
 *   envio não foi concluído (ler não conclui).
 */
import { isAxiosError, type AxiosInstance } from 'axios';

import type {
  CasoEncontradoData,
  EscolhaDoContrato,
  IdentificadorDoContratoData,
  SituacaoDoContrato,
} from '@/lib/abertura_do_caso';
import type { ArquivoPendenteData } from '@/lib/arquivos_pendentes';

export interface CasoDoEnvioData {
  case_id: string;
  codigo: string;
  chave: string;
  nome: string;
  identificador: IdentificadorDoContratoData | null;
  situacao: SituacaoDoContrato;
  /** O contrato que ficou no caso de entrada (o primeiro novo). */
  de_entrada: boolean;
  separacao_reprovada: boolean;
  /**
   * E165.F1.T06: o caso novo aberto para a CCB que já tinha caso (a nova análise), com o caso de
   * origem. No `complemento`, o envio foi vinculado ao existente. Ausente em resposta antiga.
   */
  origem?: CasoEncontradoData | null;
  /**
   * E177.F5 (GUS-253): os documentos que o contrato levou ao caso e, deles, os que outro contrato
   * do envio também levou. Ausentes (ou 0) no recibo gravado antes.
   */
  documentos?: number;
  compartilhados?: number;
  /**
   * E177.F5.C01 (GUS-253 item 4): a chave lida no pacote diante da qual quem enviou manteve o
   * número informado. Ausente no recibo gravado antes.
   */
  divergencia_mantida?: IdentificadorDoContratoData | null;
  /**
   * E193.F1 (GUS-259): o que o kit do caso digital ainda espera, lido do caso na leitura do recibo
   * (a conferência roda logo depois da conclusão). Ausente ou vazio = nada pendente.
   */
  arquivos_pendentes?: ArquivoPendenteData[];
  /**
   * E193.F1.C10 (F-E193.F1-06): onde está a conferência do kit do caso. Em andamento, a lista
   * pode ainda mudar; a leitura com a que falhou pede outra e devolve "em andamento". Ausente no
   * recibo de servidor antigo (= concluída).
   */
  conferencia_do_kit?: EstadoDaConferenciaDoKit;
}

export type EstadoDaConferenciaDoKit = 'em_andamento' | 'concluida' | 'falhou';

export interface ConclusaoDaAberturaData {
  case_id: string;
  concluida_em: string;
  casos: CasoDoEnvioData[];
  /** O caso de entrada ficou sem contrato próprio e foi encerrado. */
  caso_de_entrada_encerrado: boolean;
}

/**
 * Por que o servidor recusou concluir; `pergunta` traz as unidades sem destino. E165.F1.T04: a
 * identificação do cliente incompleta e o CPF informado que diverge do lido (422). E165.F1.T06:
 * falta a escolha de alguma CCB que já tem caso (422; `unidades` traz as chaves dessas CCBs).
 */
export type MotivoDaRecusa =
  | 'pergunta'
  | 'em_leitura'
  | 'sem_arquivo'
  | 'ccb_sem_arquivo'
  | 'ccb_divergente'
  | 'identificacao'
  | 'cpf_divergente'
  | 'escolha'
  // E206.F1 (GUS-296 item 1): a falha do servidor (5xx) e a falta de conexão dizem o que houve e
  // que o envio continua no rascunho — antes viravam "Tente novamente", que não resolvia.
  | 'servidor'
  | 'rede'
  | 'outro';

export class ConclusaoRecusadaError extends Error {
  constructor(
    readonly motivo: MotivoDaRecusa,
    readonly unidades: readonly string[] = [],
  ) {
    super(`conclusão recusada: ${motivo}`);
    this.name = 'ConclusaoRecusadaError';
  }
}

function textos(lista: unknown): string[] {
  return Array.isArray(lista) ? lista.filter((u): u is string => typeof u === 'string') : [];
}

function recusaDe422(detalhe: unknown): ConclusaoRecusadaError {
  if (detalhe === 'CPF_DIVERGENTE') return new ConclusaoRecusadaError('cpf_divergente');
  const { code, ccbs, campos } = (detalhe ?? {}) as {
    code?: unknown;
    ccbs?: unknown;
    campos?: unknown;
  };
  if (code === 'ESCOLHA_OBRIGATORIA') return new ConclusaoRecusadaError('escolha', textos(ccbs));
  // E177.F1.C03 (GUS-253 item 3): os campos que faltam, para a mensagem não pedir o já preenchido.
  if (code === 'IDENTIFICACAO_OBRIGATORIA')
    return new ConclusaoRecusadaError('identificacao', textos(campos));
  // E177.F5 (ADR-0130 §2.4, §2.6): a CCB declarada sem arquivo e a divergente sem decisão, com as
  // chaves das CCBs.
  if (code === 'CCB_SEM_ARQUIVO')
    return new ConclusaoRecusadaError('ccb_sem_arquivo', textos(ccbs));
  if (code === 'CCB_DIVERGENTE') return new ConclusaoRecusadaError('ccb_divergente', textos(ccbs));
  return new ConclusaoRecusadaError('outro');
}

/** A recusa pela resposta do servidor. E206.F1 (GUS-296 item 1): o 5xx diz que foi o servidor. */
function recusaDaResposta(resposta: { status: number; data: unknown }): ConclusaoRecusadaError {
  if (resposta.status >= 500) return new ConclusaoRecusadaError('servidor');
  const detalhe: unknown = (resposta.data as { detail?: unknown } | undefined)?.detail;
  if (resposta.status === 422) return recusaDe422(detalhe);
  if (resposta.status !== 409) return new ConclusaoRecusadaError('outro');
  if (typeof detalhe === 'object' && detalhe !== null) {
    const { code, unidades } = detalhe as { code?: unknown; unidades?: unknown };
    if (code === 'ABERTURA_COM_PERGUNTA') {
      return new ConclusaoRecusadaError('pergunta', textos(unidades));
    }
  }
  if (detalhe === 'ABERTURA_EM_LEITURA') return new ConclusaoRecusadaError('em_leitura');
  if (detalhe === 'ABERTURA_SEM_ARQUIVO') return new ConclusaoRecusadaError('sem_arquivo');
  return new ConclusaoRecusadaError('outro');
}

/**
 * A recusa do servidor pelo motivo; também a do pedido de conclusão (E189.F2). E206.F1: sem
 * resposta, a conexão.
 */
export function recusaDaConclusao(err: unknown): ConclusaoRecusadaError {
  if (!isAxiosError(err)) return new ConclusaoRecusadaError('outro');
  return err.response === undefined
    ? new ConclusaoRecusadaError('rede')
    : recusaDaResposta(err.response);
}

export function ehConclusao(data: unknown): data is ConclusaoDaAberturaData {
  const corpo = data as Partial<ConclusaoDaAberturaData> | null;
  return typeof corpo === 'object' && corpo !== null && Array.isArray(corpo.casos);
}

/**
 * E177.F1.C04 (homologação de 29/10, GT-2026-0272): a conclusão espera o processamento do envio em
 * curso (a leitura da CCB, a facial, a verificação das peças) e passava dos 15 s do cliente — a tela
 * dizia "Não foi possível concluir" enquanto o servidor concluía. O mesmo teto do envio de arquivos.
 */
export const CONCLUSAO_TIMEOUT_MS = 10 * 60_000;

/** E165.F1.T06: `escolhas` é a de cada CCB que já tem caso, pela chave do contrato. */
export async function concluirAbertura(
  client: AxiosInstance,
  caseId: string,
  respostas: Readonly<Record<string, string>>,
  escolhas: Readonly<Record<string, EscolhaDoContrato>> = {},
): Promise<ConclusaoDaAberturaData> {
  let data: unknown;
  try {
    ({ data } = await client.post(
      `/casos/${caseId}/abertura/conclusao`,
      { respostas, escolhas },
      { timeout: CONCLUSAO_TIMEOUT_MS },
    ));
  } catch (err) {
    throw recusaDaConclusao(err);
  }
  if (!ehConclusao(data)) throw new ConclusaoRecusadaError('outro');
  return data;
}

export async function lerConclusao(
  client: AxiosInstance,
  caseId: string,
): Promise<ConclusaoDaAberturaData | null> {
  try {
    const { data } = await client.get<unknown>(`/casos/${caseId}/abertura/conclusao`);
    if (!ehConclusao(data)) throw new Error('resposta fora do contrato');
    return data;
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === 404) return null;
    throw err;
  }
}
